import React, { useState, useEffect } from 'react';
import { IconPlayerPlayFilled, IconMessageCircleFilled, IconTrashFilled, IconPlus } from '@tabler/icons-react';
import logger from '../../../utils/logger';
import projectService from '../../../services/projectService';
import { Spinner } from '../../ui';

const formatSegmentTime = (seconds) => {
  const s = Number(seconds) || 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const t = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${t.toFixed(3)}`;
};

/**
 * Editable transcription box; always in edit mode when transcript is present.
 * When transcript_segments is present, displays timestamped segment textareas; otherwise plain textarea.
 * Optional videoCurrentTime and onSegmentSeek for video sync (same as read-only boxes).
 */
const EditableTranscriptBox = ({
  transcript,
  projectId,
  onSaved,
  title = 'Editable transcript',
  disabled = false,
  /** For standalone KG projects: "google_stt" | "whisper" – saves via PATCH standalone-transcript. "final_editable" saves via PATCH final-editable-transcript. */
  source = null,
  /** When true, show pastel green background (PM viewing reviewer-approved version) */
  highlightGreen = false,
  videoCurrentTime,
  onSegmentSeek,
  /** When true, no card/header (for use inside page section). */
  embedded = false,
  /** When true, hide timestamped segments and video sync (plain text / textarea only). */
  simpleMode = false,
  /** When true, show transcript as read-only: no edit, no tool icons (comment, delete, play, add segment). */
  readOnly = false,
}) => {
  const initialText = transcript?.transcript_text || transcript?.text || '';
  const segments = simpleMode ? null : (Array.isArray(transcript?.transcript_segments) ? transcript.transcript_segments : null);
  const hasSegments = segments && segments.length > 0;
  const showPlaceholder = !transcript && !disabled;
  const [isEditing, setIsEditing] = useState(true);
  const [value, setValue] = useState(initialText);
  const [editedSegments, setEditedSegments] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [savingSegmentIndex, setSavingSegmentIndex] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [commentEditingIndex, setCommentEditingIndex] = useState(null);
  const isFinalEditable = source === 'final_editable';
  const isStandalone = !transcript?.id && (source === 'google_stt' || source === 'whisper' || isFinalEditable);

  useEffect(() => {
    setValue(initialText);
  }, [initialText]);

  const prevEditingRef = React.useRef(false);
  useEffect(() => {
    if (isEditing && hasSegments && segments) {
      if (!prevEditingRef.current || editedSegments === null || (editedSegments.length !== segments.length)) {
        setEditedSegments(segments.map((s) => ({ start: s.start, end: s.end, text: s.text || '', comment: s.comment ?? '' })));
      }
    } else if (!isEditing) {
      setEditedSegments(null);
      setCommentEditingIndex(null);
    }
    prevEditingRef.current = isEditing;
  }, [isEditing, hasSegments, segments, editedSegments]);

  useEffect(() => {
    if (hasSegments && editedSegments && segments) {
      const differ =
        editedSegments.length !== segments.length ||
        editedSegments.some((s, i) => (s.text || '') !== (segments[i]?.text || '') || (s.comment ?? '') !== (segments[i]?.comment ?? ''));
      setHasUnsavedChanges(differ);
    } else {
      setHasUnsavedChanges(value !== initialText);
    }
  }, [value, initialText, hasSegments, editedSegments, segments]);

  const getTextToSave = () => {
    if (hasSegments && editedSegments?.length) {
      return editedSegments.map((s) => s.text || '').join('\n');
    }
    return value;
  };

  const getFinalEditablePayload = () => {
    const transcript_text = getTextToSave();
    if (isFinalEditable && hasSegments && editedSegments?.length) {
      const segmentsForApi = editedSegments.map((s) => ({
        start: s.start,
        end: s.end,
        text: s.text || '',
        ...(s.comment != null && s.comment !== '' && { comment: s.comment }),
      }));
      const withComments = segmentsForApi.filter((s) => s.comment != null && s.comment !== '');
      if (withComments.length > 0) {
        logger.log('[EditableTranscriptBox] getFinalEditablePayload: segments with comment', withComments.length, withComments.map((s, i) => ({ i, comment: s.comment })));
      }
      return { transcript_text, transcript_segments: segmentsForApi };
    }
    return { transcript_text };
  };

  const handleSave = async (options = {}) => {
    const exitEditMode = options.exitEditMode !== false;
    const segmentIndex = options.segmentIndex;
    const forceSave = options.forceSave === true;
    if (!projectId) {
      if (exitEditMode) setIsEditing(false);
      return;
    }
    if (!forceSave && !hasUnsavedChanges) {
      if (exitEditMode) setIsEditing(false);
      return;
    }
    setSavingSegmentIndex(segmentIndex ?? null);
    const textToSave = getTextToSave();
    if (isFinalEditable) {
      setIsSaving(true);
      try {
        const payload = getFinalEditablePayload();
        if (payload.transcript_segments?.some((s) => s.comment != null && s.comment !== '')) {
          logger.log('[EditableTranscriptBox] handleSave: sending payload with comments', payload.transcript_segments?.map((s, i) => ({ i, comment: s.comment })));
        }
        await projectService.updateFinalEditableTranscript(projectId, payload);
        if (exitEditMode && onSaved) onSaved();
        setValue(textToSave);
        if (exitEditMode) setIsEditing(false);
        setHasUnsavedChanges(false);
      } catch (err) {
        logger.error('Error saving final editable transcript:', err);
        alert(err?.response?.data?.detail || 'Failed to save transcript.');
      } finally {
        setIsSaving(false);
        setSavingSegmentIndex(null);
      }
      return;
    }
    if (isStandalone && source && source !== 'final_editable') {
      setIsSaving(true);
      try {
        await projectService.updateStandaloneTranscript(projectId, source, textToSave);
        if (exitEditMode && onSaved) onSaved();
        setValue(textToSave);
        if (exitEditMode) setIsEditing(false);
        setHasUnsavedChanges(false);
      } catch (err) {
        logger.error('Error saving standalone transcript:', err);
        alert(err?.response?.data?.detail || 'Failed to save transcript.');
      } finally {
        setIsSaving(false);
        setSavingSegmentIndex(null);
      }
      return;
    }
    if (!transcript?.id) {
      if (exitEditMode) setIsEditing(false);
      setSavingSegmentIndex(null);
      return;
    }
    setIsSaving(true);
    try {
      await projectService.updateTranscript(projectId, transcript.id, { transcript_text: textToSave });
      if (exitEditMode && onSaved) onSaved();
      setValue(textToSave);
      if (exitEditMode) setIsEditing(false);
      setHasUnsavedChanges(false);
    } catch (err) {
      logger.error('Error saving transcript:', err);
      alert(err?.response?.data?.detail || 'Failed to save transcript.');
    } finally {
      setIsSaving(false);
      setSavingSegmentIndex(null);
    }
  };

  const handleBlur = () => {};

  const showEditableSegments = hasSegments && editedSegments;
  const showTextarea = !showPlaceholder && !hasSegments;

  const segmentEditContainerRef = React.useRef(null);
  const handleSegmentAreaBlur = (blurredSegmentIndex) => {
    setTimeout(() => {
      if (!segmentEditContainerRef.current) return;
      if (segmentEditContainerRef.current.contains(document.activeElement)) {
        if (hasUnsavedChanges && projectId && (transcript?.id || isStandalone || isFinalEditable)) {
          handleSave({ exitEditMode: false, segmentIndex: blurredSegmentIndex });
        }
      } else {
        handleBlur();
      }
    }, 0);
  };

  const contentArea = (
    <div
      className={`flex-1 min-h-0 overflow-y-auto ${
        embedded ? 'p-2' : 'rounded-b-lg p-3 border-t-0 border-border max-h-[320px]'
      } ${
        highlightGreen ? 'bg-green-50/80 dark:bg-green-950/20' : embedded ? '' : 'ring-2 ring-ring'
      } ${disabled ? 'cursor-not-allowed opacity-70' : 'cursor-text'}`}
    >
      {showPlaceholder && (
        <p className="text-muted-foreground text-sm italic">Transcript not available</p>
      )}
      {showEditableSegments && (
        <div ref={segmentEditContainerRef} onClick={(e) => e.stopPropagation()} className="space-y-0">
          <ul className="space-y-0 list-none p-0 m-0">
            {editedSegments.map((seg, i) => (
              <li key={i} className="text-sm rounded-md px-2 py-1 -mx-2 -my-0.5 bg-background">
                <div className="flex items-center gap-1.5 justify-between">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-muted-foreground font-mono text-xs shrink-0">
                      {formatSegmentTime(seg.start)} – {formatSegmentTime(seg.end)}
                    </span>
                    {isSaving && savingSegmentIndex === i && (
                      <Spinner size="xs" className="text-primary shrink-0" />
                    )}
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    {!readOnly && (
                      <>
                        <button
                          type="button"
                          disabled={disabled || isSaving}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setCommentEditingIndex((prev) => (prev === i ? null : i));
                          }}
                          className={`shrink-0 p-1 rounded focus:outline-none ${seg.comment ? 'text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
                          title={seg.comment ? 'Edit comment' : 'Add comment'}
                        >
                          <IconMessageCircleFilled className="w-3.5 h-3.5" stroke={1.5} />
                        </button>
                        <button
                      type="button"
                      disabled={disabled || isSaving}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const next = editedSegments.filter((_, idx) => idx !== i);
                        setEditedSegments(next);
                        setCommentEditingIndex(null);
                        if (projectId && (isFinalEditable || isStandalone)) {
                          const textToSave = next.map((s) => s.text || '').join('\n');
                          const payload = isFinalEditable
                            ? { transcript_text: textToSave, transcript_segments: next.map((s) => ({ start: s.start, end: s.end, text: s.text || '', ...(s.comment && { comment: s.comment }) })) }
                            : { transcript_text: textToSave };
                          if (isFinalEditable) {
                            projectService.updateFinalEditableTranscript(projectId, payload).then(() => { setHasUnsavedChanges(false); onSaved?.(); }).catch((err) => {
                              logger.error('Error saving after delete segment:', err);
                              alert(err?.response?.data?.detail || 'Failed to save.');
                            });
                          } else {
                            projectService.updateStandaloneTranscript(projectId, source, textToSave).then(() => { setHasUnsavedChanges(false); onSaved?.(); }).catch((err) => {
                              logger.error('Error saving after delete segment:', err);
                              alert(err?.response?.data?.detail || 'Failed to save.');
                            });
                          }
                        }
                      }}
                      className="shrink-0 p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 focus:outline-none"
                      title="Delete segment"
                    >
                      <IconTrashFilled className="w-3.5 h-3.5" stroke={1.5} />
                        </button>
                      </>
                    )}
                    {typeof onSegmentSeek === 'function' && (
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSegmentSeek(seg.start, seg.end); }}
                        className="shrink-0 p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted focus:outline-none"
                        title="Play from here"
                      >
                        <IconPlayerPlayFilled className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
                <textarea
                  rows={1}
                  readOnly={readOnly}
                  disabled={readOnly || isSaving}
                  className={`w-full mt-1 text-sm text-foreground border border-border rounded-sm px-2 py-1.5 resize-none overflow-y-auto focus:ring-2 focus:ring-ring focus:outline-none resize-y ${readOnly ? 'bg-muted/60 dark:bg-muted/40' : 'bg-surface'}`}
                  value={seg.text}
                  onChange={(e) => {
                    if (readOnly) return;
                    const next = editedSegments.slice();
                    next[i] = { ...next[i], text: e.target.value };
                    setEditedSegments(next);
                  }}
                  onBlur={() => handleSegmentAreaBlur(i)}
                  placeholder="Segment text"
                />
                {!readOnly && commentEditingIndex === i ? (
                  <div className="mt-1">
                    <input
                      type="text"
                      className="w-full text-xs text-foreground bg-muted/50 border border-border rounded px-2 py-1.5 focus:ring-2 focus:ring-ring focus:outline-none"
                      value={seg.comment ?? ''}
                      onChange={(e) => {
                        const next = editedSegments.slice();
                        next[i] = { ...next[i], comment: e.target.value };
                        setEditedSegments(next);
                      }}
                      onBlur={() => {
                        setCommentEditingIndex(null);
                        logger.log('[EditableTranscriptBox] comment blur: segmentIndex=%s hasUnsavedChanges=%s projectId=%s isFinalEditable=%s', i, hasUnsavedChanges, projectId, isFinalEditable);
                        if (projectId && isFinalEditable) {
                          handleSave({ exitEditMode: false, segmentIndex: i, forceSave: true });
                        } else if (hasUnsavedChanges && projectId && (transcript?.id || isStandalone)) {
                          handleSave({ exitEditMode: false, segmentIndex: i });
                        }
                      }}
                      placeholder="Add a comment..."
                      autoFocus
                    />
                  </div>
                ) : seg.comment ? (
                  <p className="mt-1 text-xs text-muted-foreground italic pl-0.5">{seg.comment}</p>
                ) : null}
              </li>
            ))}
          </ul>
          {!readOnly && !disabled && !isSaving && (
            <div className="mt-2 pt-2 border-t border-border">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const last = editedSegments[editedSegments.length - 1];
                  const start = last ? Number(last.end) : 0;
                  const end = last ? Number(last.end) + 5 : 5;
                  const next = [...editedSegments, { start, end, text: '', comment: '' }];
                  setEditedSegments(next);
                  if (projectId && isFinalEditable) {
                    const textToSave = next.map((s) => s.text || '').join('\n');
                    projectService.updateFinalEditableTranscript(projectId, {
                      transcript_text: textToSave,
                      transcript_segments: next.map((s) => ({ start: s.start, end: s.end, text: s.text || '', ...(s.comment && { comment: s.comment }) })),
                    }).then(() => { setHasUnsavedChanges(false); onSaved?.(); }).catch((err) => {
                      logger.error('Error saving after add segment:', err);
                      alert(err?.response?.data?.detail || 'Failed to save.');
                    });
                  }
                }}
                className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded px-2 py-1.5 focus:outline-none"
                title="Add segment"
              >
                <IconPlus className="w-4 h-4" stroke={2} />
                Add segment
              </button>
            </div>
          )}
        </div>
      )}
      {showTextarea && (
        <div className="mt-3 border-t border-border pt-3" onClick={(e) => e.stopPropagation()}>
          <textarea
            className={`w-full min-h-[120px] text-sm text-foreground border border-border rounded-sm p-2 resize-y focus:ring-2 focus:ring-ring focus:outline-none ${readOnly ? 'bg-muted/60 dark:bg-muted/40' : 'bg-input'}`}
            value={value}
            onChange={(e) => { if (!readOnly) setValue(e.target.value); }}
            onBlur={handleBlur}
            disabled={readOnly || isSaving}
            readOnly={readOnly}
            autoFocus={!readOnly}
            placeholder="Edit transcript..."
          />
        </div>
      )}
    </div>
  );

  if (embedded) {
    return (
      <div className="flex flex-col flex-1 min-h-0">

        {contentArea}
      </div>
    );
  }

  return (
    <div className={`rounded-lg flex flex-col min-h-[200px] shadow-subtle-sm ${highlightGreen ? 'bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800' : 'bg-card border border-border'}`}>
      <div className="flex items-center justify-between p-2 border-b border-border flex-shrink-0">
        <h3 className="text-card-foreground text-sm font-semibold">{title}</h3>
        {isSaving && (
          <div className="flex items-center gap-2">
            <Spinner size="xs" className="text-primary" />
            <span className="text-muted-foreground text-xs">Saving...</span>
          </div>
        )}
      </div>
      {contentArea}
    </div>
  );
};

export default EditableTranscriptBox;
